import React, { useEffect, useMemo, useState } from 'react';
import { getErrorMessage } from '../utils/errorHandler';

interface TrialBalanceRow {
  kamoku_id: number;
  kamoku: string;
  kamoku_code?: string;
  daikubun: string;
  hierarchy: number;
  zengetsu_karikata: number;
  zengetsu_kashikata: number;
  shikin_tougetsu_karikata: number;
  shikin_tougetsu_kashikata: number;
  jigyou_tougetsu_karikata: number;
  jigyou_tougetsu_kashikata: number;
  hojo_id?: number | null;
  hojo_name?: string | null;
  hojo_code?: string | null;
}

interface TrialBalanceResponse {
  data: TrialBalanceRow[];
  count: number;
}

interface TrialBalanceProps {
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentTenantId: number | null;
  tenantName: string;
  onNavigate?: (menu: string, params?: any) => void;
}

const TrialBalance: React.FC<TrialBalanceProps> = ({ apiFetch, currentTenantId, tenantName, onNavigate }) => {
  const handleDrillDown = (kamokuId: number) => {
    if (onNavigate) {
      onNavigate('sub-ledger', { kamokuId, targetDate });
    }
  };
  const today = useMemo(() => new Date(), []);
  const [targetDate, setTargetDate] = useState(() => today.toISOString().split('T')[0]);
  const [rows, setRows] = useState<TrialBalanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTrialBalance = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/reports/trial-balance?target_date=${targetDate}`);
      if (!response.ok) {
        const message = await getErrorMessage(response, '試算表の取得に失敗しました');
        throw new Error(message);
      }
      const json = (await response.json()) as TrialBalanceResponse;
      setRows(json.data ?? []);
    } catch (err: any) {
      setError(err?.message || '試算表の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrialBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDate, currentTenantId]);

  const formatNumber = (num: number) =>
    new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 }).format(num ?? 0);

  const totals = rows.reduce(
    (acc, row) => {
      acc.zengetsu_karikata += row.zengetsu_karikata || 0;
      acc.zengetsu_kashikata += row.zengetsu_kashikata || 0;
      acc.shikin_tougetsu_karikata += row.shikin_tougetsu_karikata || 0;
      acc.shikin_tougetsu_kashikata += row.shikin_tougetsu_kashikata || 0;
      acc.jigyou_tougetsu_karikata += row.jigyou_tougetsu_karikata || 0;
      acc.jigyou_tougetsu_kashikata += row.jigyou_tougetsu_kashikata || 0;
      return acc;
    },
    {
      zengetsu_karikata: 0,
      zengetsu_kashikata: 0,
      shikin_tougetsu_karikata: 0,
      shikin_tougetsu_kashikata: 0,
      jigyou_tougetsu_karikata: 0,
      jigyou_tougetsu_kashikata: 0,
    }
  );

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        border: '2px inset #d0d0d0',
        padding: '16px',
        fontSize: '12px',
      }}
    >
      <div style={{ marginBottom: '12px', fontSize: '12px', fontWeight: 'bold', color: '#333' }}>
        保育園: {tenantName || 'テナント未選択'}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <label style={{ fontWeight: 'bold' }}>対象日</label>
        <input
          type="date"
          value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
          style={{
            padding: '4px 6px',
            border: '2px inset #f0f0f0',
            backgroundColor: '#fff',
            fontFamily: "'MS UI Gothic', sans-serif",
            fontSize: '12px',
          }}
        />
        <button
          onClick={fetchTrialBalance}
          disabled={loading}
          style={{
            backgroundColor: '#e0e0e0',
            border: '2px outset #e0e0e0',
            padding: '4px 12px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: "'MS UI Gothic', sans-serif",
            fontSize: '12px',
          }}
          onMouseDown={(e) => (e.currentTarget.style.border = '2px inset #e0e0e0')}
          onMouseUp={(e) => (e.currentTarget.style.border = '2px outset #e0e0e0')}
          onMouseLeave={(e) => (e.currentTarget.style.border = '2px outset #e0e0e0')}
        >
          {loading ? '集計中...' : '集計'}
        </button>
      </div>

      {error && (
        <div
          style={{
            backgroundColor: '#fee',
            border: '1px solid #f99',
            padding: '8px',
            marginBottom: '16px',
          }}
        >
          {error}
        </div>
      )}

      <div style={{
        overflow: 'auto',
        maxHeight: '60vh',
      }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: "'MS UI Gothic', sans-serif",
          }}
        >
          <thead>
            <tr>
              <th className="classic-th" style={{ position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>大区分</th>
              <th className="classic-th" style={{ position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>勘定科目</th>
              <th className="classic-th" style={{ position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>前月 借方</th>
              <th className="classic-th" style={{ position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>前月 貸方</th>
              <th className="classic-th" style={{ position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>資金 当月借方</th>
              <th className="classic-th" style={{ position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>資金 当月貸方</th>
              <th className="classic-th" style={{ position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>事業 当月借方</th>
              <th className="classic-th" style={{ position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 2 }}>事業 当月貸方</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const isHojo = !!row.hojo_id;
              const uniqueKey = isHojo ? `${row.kamoku_id}-${row.hojo_id}` : `${row.kamoku_id}-main-${index}`;

              return (
                <tr key={uniqueKey} style={{ backgroundColor: isHojo ? '#f9f9f9' : 'inherit' }}>
                  <td className="classic-td" style={{ whiteSpace: 'nowrap' }}>
                    {!isHojo ? row.daikubun : ''}
                  </td>
                  <td
                    className="classic-td"
                    style={{ paddingLeft: isHojo ? '30px' : `${Math.min(row.hierarchy, 5) * 12}px` }}
                  >
                    {isHojo ? (
                      <>
                        {row.hojo_code ? `[${row.hojo_code}] ` : ''}
                        {row.hojo_name}
                      </>
                    ) : (
                      <>
                        {row.kamoku_code ? `[${row.kamoku_code}] ` : ''}
                        {row.kamoku}
                      </>
                    )}
                  </td>
                  <td
                    className="classic-td"
                    style={{ textAlign: 'right', cursor: 'pointer', color: '#0066cc' }}
                    onClick={() => handleDrillDown(row.kamoku_id)}
                  >
                    {row.zengetsu_karikata ? formatNumber(row.zengetsu_karikata) : ''}
                  </td>
                  <td
                    className="classic-td"
                    style={{ textAlign: 'right', cursor: 'pointer', color: '#0066cc' }}
                    onClick={() => handleDrillDown(row.kamoku_id)}
                  >
                    {row.zengetsu_kashikata ? formatNumber(row.zengetsu_kashikata) : ''}
                  </td>
                  <td
                    className="classic-td"
                    style={{ textAlign: 'right', cursor: 'pointer', color: '#0066cc' }}
                    onClick={() => handleDrillDown(row.kamoku_id)}
                  >
                    {row.shikin_tougetsu_karikata ? formatNumber(row.shikin_tougetsu_karikata) : ''}
                  </td>
                  <td
                    className="classic-td"
                    style={{ textAlign: 'right', cursor: 'pointer', color: '#0066cc' }}
                    onClick={() => handleDrillDown(row.kamoku_id)}
                  >
                    {row.shikin_tougetsu_kashikata ? formatNumber(row.shikin_tougetsu_kashikata) : ''}
                  </td>
                  <td
                    className="classic-td"
                    style={{ textAlign: 'right', cursor: 'pointer', color: '#0066cc' }}
                    onClick={() => handleDrillDown(row.kamoku_id)}
                  >
                    {row.jigyou_tougetsu_karikata ? formatNumber(row.jigyou_tougetsu_karikata) : ''}
                  </td>
                  <td
                    className="classic-td"
                    style={{ textAlign: 'right', cursor: 'pointer', color: '#0066cc' }}
                    onClick={() => handleDrillDown(row.kamoku_id)}
                  >
                    {row.jigyou_tougetsu_kashikata
                      ? formatNumber(row.jigyou_tougetsu_kashikata)
                      : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
              <td className="classic-td" colSpan={2} style={{ textAlign: 'center', position: 'sticky', bottom: 0, backgroundColor: '#f0f0f0', zIndex: 2 }}>
                合計
              </td>
              <td className="classic-td" style={{ textAlign: 'right', position: 'sticky', bottom: 0, backgroundColor: '#f0f0f0', zIndex: 2 }}>
                {totals.zengetsu_karikata ? formatNumber(totals.zengetsu_karikata) : ''}
              </td>
              <td className="classic-td" style={{ textAlign: 'right', position: 'sticky', bottom: 0, backgroundColor: '#f0f0f0', zIndex: 2 }}>
                {totals.zengetsu_kashikata ? formatNumber(totals.zengetsu_kashikata) : ''}
              </td>
              <td className="classic-td" style={{ textAlign: 'right', position: 'sticky', bottom: 0, backgroundColor: '#f0f0f0', zIndex: 2 }}>
                {totals.shikin_tougetsu_karikata ? formatNumber(totals.shikin_tougetsu_karikata) : ''}
              </td>
              <td className="classic-td" style={{ textAlign: 'right', position: 'sticky', bottom: 0, backgroundColor: '#f0f0f0', zIndex: 2 }}>
                {totals.shikin_tougetsu_kashikata ? formatNumber(totals.shikin_tougetsu_kashikata) : ''}
              </td>
              <td className="classic-td" style={{ textAlign: 'right', position: 'sticky', bottom: 0, backgroundColor: '#f0f0f0', zIndex: 2 }}>
                {totals.jigyou_tougetsu_karikata ? formatNumber(totals.jigyou_tougetsu_karikata) : ''}
              </td>
              <td className="classic-td" style={{ textAlign: 'right', position: 'sticky', bottom: 0, backgroundColor: '#f0f0f0', zIndex: 2 }}>
                {totals.jigyou_tougetsu_kashikata ? formatNumber(totals.jigyou_tougetsu_kashikata) : ''}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default TrialBalance;
